/**
 * Demo Center — Industry Profiles Edition
 *
 * 5 industry-specific profiles + Executive Demo with custom selection.
 * The profile catalog is fetched from /api/demo/profiles so the UI stays
 * in sync with backend without redeploys.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, RefreshCw, Trash2, Wand2, AlertTriangle,
  CheckCircle2, ArrowRight, Database, Home, Building2, Hospital,
  Hammer, Factory, ListChecks,
} from 'lucide-react';

const ICON_MAP = {
  home: Home, building: Building2, hospital: Hospital,
  bridge: Hammer, factory: Factory,
};

const TONE_MAP = {
  rose:     { border: 'border-rose-200',     bg: 'bg-rose-50',     icon: 'bg-rose-100 text-rose-700' },
  amber:    { border: 'border-amber-200',    bg: 'bg-amber-50',    icon: 'bg-amber-100 text-amber-700' },
  indigo:   { border: 'border-indigo-200',   bg: 'bg-indigo-50',   icon: 'bg-indigo-100 text-indigo-700' },
  sky:      { border: 'border-sky-200',      bg: 'bg-sky-50',      icon: 'bg-sky-100 text-sky-700' },
  emerald:  { border: 'border-emerald-200',  bg: 'bg-emerald-50',  icon: 'bg-emerald-100 text-emerald-700' },
};

const TRUST_LABEL_AR = {
  low: 'منخفض', 'medium-low': 'متوسط منخفض',
  medium: 'متوسط', high: 'مرتفع', excellent: 'ممتاز',
};

const DemoCenter = () => {
  const [status, setStatus] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedSet, setSelectedSet] = useState(new Set());
  const [busy, setBusy] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Load profile catalog + demo status
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [s, p] = await Promise.all([
          axiosInstance.get('/demo/status'),
          axiosInstance.get('/demo/profiles'),
        ]);
        if (!cancelled) {
          setStatus(s.data);
          setProfiles(p.data.profiles || []);
        }
      } catch (e) {
        if (!cancelled) toast.error('فشل تحميل بيانات Demo Center');
      }
    };
    run();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  const run = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
      reload();
    } finally { setBusy(null); }
  };

  const executive = () => run('executive', async () => {
    const r = await axiosInstance.post('/demo/executive', {});
    const s = r.data.scan_stats;
    toast.success(
      `Executive Demo جاهز! ${r.data.projects.length} مشاريع · ${s.decisions_total} قرار · ${r.data.auto_pprs_created} PPR تلقائية`,
      { duration: 6000 }
    );
  });

  const buildCustom = () => run('custom', async () => {
    const list = Array.from(selectedSet);
    if (list.length === 0) {
      toast.warning('اختر قطاعاً واحداً على الأقل');
      return;
    }
    const r = await axiosInstance.post('/demo/executive', { profiles: list });
    const s = r.data.scan_stats;
    toast.success(
      `تم بناء عرض مخصص · ${r.data.projects.length} قطاعات · ${s.decisions_total} قرار`,
      { duration: 6000 }
    );
    setSelectedSet(new Set());
  });

  const buildOne = (key) => run(key, async () => {
    const r = await axiosInstance.post(`/demo/build/${key}`);
    toast.success(`تم بناء "${r.data.project.name}" بـ ${r.data.scan_stats.decisions_total} قرار`);
  });

  const reset = () => run('reset', async () => {
    if (!window.confirm('سيتم حذف بيانات Demo الحالية وإعادة بنائها. متابعة؟')) return;
    const r = await axiosInstance.post('/demo/reset');
    toast.success(`تم! حذف ${Object.values(r.data.wiped).reduce((a, b) => a + b, 0)} وثيقة + بناء Executive جديد`);
  });

  const wipe = () => run('wipe', async () => {
    if (!window.confirm('سيتم حذف كل بيانات الـ Demo نهائياً. متابعة؟')) return;
    const r = await axiosInstance.delete('/demo/wipe');
    const total = Object.values(r.data).reduce((a, b) => a + (b > 0 ? b : 0), 0);
    toast.success(`تم حذف ${total} وثيقة demo`);
  });

  const toggleSelected = (key) => {
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedSet(next);
  };

  const totalDemo = status?.total_demo_docs || 0;
  const hasDemo = !!status?.has_demo_data;
  const selectedCount = selectedSet.size;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="demo-center-page">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="text-xs text-indigo-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Demo Center · Industry-Aware ERP
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            مركز الديمو · يتكيّف مع قطاعك
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            5 ملفات صناعية جاهزة تُظهر النظام بمفردات وقرارات حقيقية لكل قطاع. كل وثيقة مختومة بـ <code>is_demo:true</code> ويمكن حذفها بنقرة.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Status banner */}
        <div
          data-testid="demo-status-banner"
          className={`rounded-xl border p-4 flex items-center gap-3 ${
            hasDemo ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className={`p-2 rounded-lg ${hasDemo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
            {hasDemo ? <CheckCircle2 className="w-5 h-5" /> : <Database className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <div className="font-semibold">
              {hasDemo
                ? `لديك بيانات Demo نشطة (${totalDemo} وثيقة)`
                : 'لا توجد بيانات Demo حالياً'}
            </div>
            {status && (
              <div className="text-xs text-gray-500 mt-1 font-mono">
                {Object.entries(status.counts)
                  .filter(([, n]) => n > 0)
                  .map(([k, n]) => `${k}=${n}`)
                  .join(' · ') || 'كل المجموعات فارغة'}
              </div>
            )}
          </div>
          <button
            data-testid="demo-refresh-status"
            onClick={reload}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs hover:bg-white flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> تحديث
          </button>
        </div>

        {/* Executive Demo — flagship */}
        <div className="rounded-2xl border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-purple-50 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="w-5 h-5 text-indigo-600" />
                <span className="text-xs text-indigo-700 font-bold uppercase tracking-wider">
                  Executive Demo · Recommended
                </span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                ابنِ بيئة عرض كاملة بنقرة واحدة
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                ينشئ 3 مشاريع افتراضية (سكني · صحي · صناعي) تغطي كامل طيف الجاهزية،
                ويشغّل S2P scan تلقائياً ليُنتج Trust Score ≈ <strong>80–100</strong>.
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                <span className="bg-white border px-2 py-1 rounded">3 مشاريع</span>
                <span className="bg-white border px-2 py-1 rounded">12 نشاطاً</span>
                <span className="bg-white border px-2 py-1 rounded">BOQ + Plans + Reservations</span>
                <span className="bg-white border px-2 py-1 rounded">~25+ S2P decisions</span>
              </div>
            </div>
            <button
              data-testid="demo-executive-btn"
              onClick={executive}
              disabled={busy === 'executive'}
              className="px-6 py-4 bg-indigo-600 text-white rounded-xl font-bold text-base hover:bg-indigo-700 disabled:bg-indigo-400 flex items-center gap-2 shadow-sm shrink-0"
            >
              {busy === 'executive' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              {busy === 'executive' ? 'جاري البناء...' : 'إنشاء Executive Demo'}
            </button>
          </div>
        </div>

        {/* Industry Profile Grid */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-lg font-bold">ملفات صناعية متخصّصة</h3>
            {selectedCount > 0 && (
              <button
                data-testid="demo-build-custom-btn"
                onClick={buildCustom}
                disabled={busy === 'custom'}
                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {busy === 'custom' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
                بناء عرض مخصّص ({selectedCount})
              </button>
            )}
          </div>

          {profiles.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="demo-profiles-loading">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              جاري تحميل الملفات الصناعية...
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="demo-profile-grid">
              {profiles.map((p) => {
                const Icon = ICON_MAP[p.icon] || Building2;
                const tone = TONE_MAP[p.tone] || TONE_MAP.indigo;
                const isSelected = selectedSet.has(p.key);
                return (
                  <div
                    key={p.key}
                    data-testid={`demo-profile-${p.key}`}
                    className={`rounded-xl border-2 ${tone.bg} ${
                      isSelected ? 'border-indigo-500 ring-2 ring-indigo-200' : tone.border
                    } p-5 flex flex-col relative transition`}
                  >
                    {/* Selection checkbox (top-right) */}
                    <label className="absolute top-3 left-3 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`demo-select-${p.key}`}
                        checked={isSelected}
                        onChange={() => toggleSelected(p.key)}
                        className="w-4 h-4 accent-indigo-600 cursor-pointer"
                      />
                    </label>

                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${tone.icon}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="font-bold text-gray-900">{p.label_ar.split(' · ')[0]}</div>
                    <div className="text-xs text-gray-600 mb-2">
                      {p.label_ar.split(' · ').slice(1).join(' · ')}
                    </div>
                    <div className="text-[10px] text-gray-500 mb-3 space-y-0.5">
                      <div>Trust: <strong>{TRUST_LABEL_AR[p.expected.trust] || p.expected.trust}</strong></div>
                      <div>Tier: <strong>{p.expected.tier_mix}</strong></div>
                      <div>PRS Band: <strong className="font-mono">{p.expected.prs_band}</strong></div>
                    </div>
                    <button
                      data-testid={`demo-build-${p.key}`}
                      onClick={() => buildOne(p.key)}
                      disabled={busy === p.key}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-1 mt-auto"
                    >
                      {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      بناء هذا القطاع
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {selectedCount > 0 && (
            <p className="text-xs text-indigo-700 mt-3" data-testid="demo-selection-hint">
              ✓ تم اختيار <strong>{selectedCount}</strong> قطاعات — اضغط
              &laquo;بناء عرض مخصّص&raquo; لإنشائها معاً في مسح S2P واحد.
            </p>
          )}
        </div>

        {/* Maintenance */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            صيانة بيانات الـ Demo
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              data-testid="demo-reset-btn"
              onClick={reset}
              disabled={busy === 'reset'}
              className="flex-1 px-4 py-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg font-semibold hover:bg-amber-100 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy === 'reset' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Reset (Wipe + Rebuild Executive)
            </button>
            <button
              data-testid="demo-wipe-btn"
              onClick={wipe}
              disabled={busy === 'wipe' || !hasDemo}
              className="flex-1 px-4 py-3 bg-rose-50 border border-rose-300 text-rose-800 rounded-lg font-semibold hover:bg-rose-100 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy === 'wipe' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Remove All Demo Data
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            تطلب العمليتان تأكيداً قبل التنفيذ. الإزالة تستهدف فقط الوثائق المختومة بـ <code>is_demo: true</code> ولا تمس البيانات الفعلية.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DemoCenter;
